import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';

interface StoreFormProps {
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  enterprises: any[];
}

const StoreForm: React.FC<StoreFormProps> = ({ onSubmit, onCancel, enterprises }) => {
  const [formData, setFormData] = useState({
    name: '',
    conceptId: '',
    franchiseeId: '',
    address: '',
    phone: '',
    email: '',
    storeType: 'company_owned',
    enterpriseId: '',
  });

  const [concepts, setConcepts] = useState<any[]>([]);
  const [franchisees, setFranchisees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchConceptsAndFranchisees = async (enterpriseId: string) => {
    if (!enterpriseId) {
      setConcepts([]);
      setFranchisees([]);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch concepts
      const conceptsResponse = await fetch(API_ENDPOINTS.concepts(enterpriseId));
      if (conceptsResponse.ok) {
        const conceptsData = await conceptsResponse.json();
        setConcepts(conceptsData.data || []);
      }

      // Fetch franchisees
      const franchiseesResponse = await fetch(API_ENDPOINTS.franchisees(enterpriseId));
      if (franchiseesResponse.ok) {
        const franchiseesData = await franchiseesResponse.json();
        setFranchisees(franchiseesData.data || []);
      }
    } catch (error) {
      console.error('Error fetching concepts and franchisees:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.enterpriseId) {
      fetchConceptsAndFranchisees(formData.enterpriseId);
    }
  }, [formData.enterpriseId]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Reset concept and franchisee when enterprise changes
      if (field === 'enterpriseId') {
        newData.conceptId = '';
        newData.franchiseeId = '';
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.enterpriseId || !formData.conceptId || !formData.address.trim()) {
      alert('Store name, enterprise, concept, and address are required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="label-modern">Enterprise *</label>
        <select
          className="input-modern"
          value={formData.enterpriseId}
          onChange={(e) => handleChange('enterpriseId', e.target.value)}
          required
        >
          <option value="">Select Enterprise</option>
          {enterprises.map((enterprise) => (
            <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
              {enterprise.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="label-modern">Concept *</label>
        <select
          className="input-modern"
          value={formData.conceptId}
          onChange={(e) => handleChange('conceptId', e.target.value)}
          required
          disabled={!formData.enterpriseId || loading}
        >
          <option value="">Select Concept</option>
          {concepts.map((concept) => (
            <option key={concept.conceptId} value={concept.conceptId}>
              {concept.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="label-modern">Store Type *</label>
        <select
          className="input-modern"
          value={formData.storeType}
          onChange={(e) => handleChange('storeType', e.target.value)}
          required
        >
          <option value="company_owned">Company Owned</option>
          <option value="franchised">Franchised</option>
        </select>
      </div>

      {formData.storeType === 'franchised' && (
        <div className="form-group">
          <label className="label-modern">Franchisee</label>
          <select
            className="input-modern"
            value={formData.franchiseeId}
            onChange={(e) => handleChange('franchiseeId', e.target.value)}
            disabled={!formData.enterpriseId || loading}
          >
            <option value="">Select Franchisee (Optional)</option>
            {franchisees.map((franchisee) => (
              <option key={franchisee.franchiseeId} value={franchisee.franchiseeId}>
                {franchisee.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label className="label-modern">Store Name *</label>
        <input
          type="text"
          className="input-modern"
          placeholder="Enter store name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label className="label-modern">Address *</label>
        <textarea
          className="input-modern"
          placeholder="Enter store address"
          rows={3}
          value={formData.address}
          onChange={(e) => handleChange('address', e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label className="label-modern">Phone</label>
        <input
          type="tel"
          className="input-modern"
          placeholder="Enter phone number"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="label-modern">Email</label>
        <input
          type="email"
          className="input-modern"
          placeholder="Enter email address"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
        />
      </div>

      <div className="modal-footer">
        <button
          type="button"
          className="btn-modern btn-modern-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-modern btn-modern-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Store'}
        </button>
      </div>
    </form>
  );
};

export default StoreForm;